/**
 * Transport — shared tempo and musical-division math.
 *
 * Modules that can tempo-sync (LFO, Delay, and eventually any time-based
 * effect) take a `Transport` reference. When their sync mode is on, they
 * derive their rate/time from `transport.bpm` and a `MusicalDivision`.
 * Changing BPM broadcasts through the standard Parameter.subscribe channel,
 * so every synced module rescales together.
 */

import { NumericParameter } from '../params/Parameter';

/**
 * Musical divisions relative to a quarter-note beat.
 *   1/1  = one whole note  = 4 beats
 *   1/4  = one quarter     = 1 beat
 *   1/4T = quarter triplet = 2/3 beats (three-in-the-space-of-two)
 *   1/8  = eighth          = 1/2 beat
 *   1/8T = eighth triplet  = 1/3 beat
 *   1/16 = sixteenth       = 1/4 beat
 *   1/16T= sixteenth triplet = 1/6 beat
 */
export type MusicalDivision =
  | '1/1'
  | '1/2'
  | '1/4'
  | '1/4T'
  | '1/8'
  | '1/8T'
  | '1/16'
  | '1/16T';

export const DIVISIONS: readonly MusicalDivision[] = [
  '1/1',
  '1/2',
  '1/4',
  '1/4T',
  '1/8',
  '1/8T',
  '1/16',
  '1/16T',
] as const;

const DIVISION_BEATS: Record<MusicalDivision, number> = {
  '1/1': 4,
  '1/2': 2,
  '1/4': 1,
  '1/4T': 2 / 3,
  '1/8': 1 / 2,
  '1/8T': 1 / 3,
  '1/16': 1 / 4,
  '1/16T': 1 / 6,
};

/**
 * A sync setting on a module — either 'free' (use the module's own native
 * rate knob) or a musical division tied to the transport's BPM.
 */
export type SyncMode = 'free' | MusicalDivision;

export const SYNC_MODES: readonly SyncMode[] = ['free', ...DIVISIONS];

export class Transport {
  readonly bpm: NumericParameter;

  constructor(defaultBpm = 120) {
    this.bpm = new NumericParameter({
      name: 'BPM',
      default: defaultBpm,
      min: 20,
      max: 300,
      curve: 'linear',
      smoothingMs: 0,
      formatter: (v) => v.toFixed(0),
    });
  }

  /** Duration in seconds of the given musical division at the current BPM. */
  divisionToSeconds(div: MusicalDivision): number {
    const beatSec = 60 / this.bpm.value;
    return beatSec * DIVISION_BEATS[div];
  }

  /** Frequency in Hz corresponding to one cycle of the given division. */
  divisionToHz(div: MusicalDivision): number {
    return 1 / this.divisionToSeconds(div);
  }
}
